import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { AlertCircle, CheckCircle } from "lucide-react";
import React, { useState } from "react";

interface ForgotPasswordFormProps {
  onSuccess?: () => void;
  onBackToLogin: () => void;
}

export const ForgotPasswordForm = ({ onSuccess, onBackToLogin }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const resp = await fetch("/api/users/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!resp.ok) {
        throw new Error("Failed to send reset email. Please try again.");
      }
      setSent(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4">
        <div className="text-center space-y-3">
          <CheckCircle className="mx-auto h-10 w-10 text-zinc-900" strokeWidth={1.25} />
          <h3 className="font-display text-2xl leading-none tracking-tight text-zinc-900">Check your email</h3>
          <p className="text-base text-zinc-500">
            If an account exists for <strong>{email}</strong>, you&apos;ll receive a
            password reset link shortly.
          </p>
        </div>
        <Button variant="ghost" className="login-ghost w-full" onClick={onBackToLogin}>
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div
          role="alert"
          className="login-error"
        >
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          {error}
        </div>
      )}
      <div>
        <label htmlFor="forgot-email" className="login-label">
          Email
        </label>
        <Input
          id="forgot-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          autoComplete="email"
          autoFocus
        />
      </div>
      <Button type="submit" className="login-primary w-full" loading={submitting}>
        {submitting ? "Sending..." : "Send reset link"}
      </Button>
    </form>
  );
};